import useSWR from "swr";
import { api } from "@dreambigger/shared/src/api/acquire";
import { LocalStorage } from "@dreambigger/shared/src/utils";
import { useAuthToken } from "../passwordless-auth";
import { useFetcher } from "./";

// All the paths for this api domain.
const basePath = "/onboarding-applications";
const paths = {
  logs: (id: string) => `${basePath}/${id}/disclosure-logs`,
};

export type DisclosureLogEventType = "viewed" | "agreed" | "declined";

/** The two event types that represent a decision, as opposed to a document open. */
export const DISCLOSURE_DECISION_EVENT_TYPES: DisclosureLogEventType[] = [
  "agreed",
  "declined",
];

export interface DisclosureLogRecord {
  uuid: string;
  eventType: DisclosureLogEventType;
  ip?: string | null;
  userAgent?: string | null;
  createdAt: string;
  // Provenance: which step/field surfaced the event.
  meta?: { stepSlug?: string; fieldKey?: string } | null;
  // The managed disclosure this row pins (its current slug + display name).
  disclosureSlug: string | null;
  label: string | null;
}

interface RecordEventArgs {
  financialDisclosureSlug: string;
  eventType: DisclosureLogEventType;
  meta?: { stepSlug?: string; fieldKey?: string };
}

/**
 * Imperative writer for disclosure log events. `viewed` fires when the applicant
 * opens the document; `agreed`/`declined` fire after the step save returns. Writes
 * are fire-and-forget: a failed event must never block progression. The functional
 * truth is the saved checkbox in `meta.responses`; this log is the defensible
 * detail (version, IP, server time) layered on top.
 *
 * Pass the real FI id once the flow resolves. It is optional (not `""`) so that,
 * before the flow loads, `useAuthToken` falls back to its own default namespace
 * rather than keying on an empty string and reading an unrelated token.
 */
export default function useDisclosureLogs(
  financialInstitutionId?: string,
  /**
   * A second source for the FI id, consulted only when the first yielded no
   * token, and called at EVENT time rather than render time.
   *
   * Admin mode needs this: its id comes from a module singleton that
   * `AdminApplicationWrapper` fills in an effect, and effects run child-first,
   * so on the render where this hook initializes the store is still empty and
   * nothing schedules a re-render when it fills. Reading it at render therefore
   * binds the token to the wrong namespace and the event is silently dropped —
   * the exact failure the admin fallback exists to prevent. By the time anything
   * is clicked, the wrapper's effect has long run.
   */
  resolveFallbackId?: () => string | null | undefined
) {
  const tokenHelper = useAuthToken(financialInstitutionId);

  const recordEvent = async (
    applicationId: string,
    { financialDisclosureSlug, eventType, meta }: RecordEventArgs
  ) => {
    if (!applicationId || !financialDisclosureSlug) return;
    // Guard, don't just narrow: skip the event until the FI id (and therefore the
    // token's namespace) has resolved. Firing before then sends `Bearer ` → 401,
    // which the catch below would swallow, silently losing the event. Once the FI
    // id is set the token is present; decisions fire after a step save, so they
    // always have it.
    let jwt = tokenHelper.getJwtToken();
    if (!jwt) {
      const fallbackId = resolveFallbackId?.();
      if (fallbackId) {
        jwt = new LocalStorage(fallbackId, true).getItem("jwt") || "";
      }
    }
    if (!jwt) return;
    try {
      await api.post(
        paths.logs(applicationId),
        { financialDisclosureSlug, eventType, meta },
        { headers: { Authorization: tokenHelper.formatAuthHeader(jwt) } }
      );
    } catch (error) {
      console.error("Failed to record disclosure event.", error);
    }
  };

  return { recordEvent };
}

/**
 * SWR reader for an application's disclosure log rows. Feeds the review-step
 * attestation summary (which pairs these with the checkbox values in meta.responses).
 */
export function useDisclosureLogList(
  financialInstitutionId?: string,
  applicationId?: string
) {
  const tokenHelper = useAuthToken(financialInstitutionId);
  const { fetchWithToken } = useFetcher(api, financialInstitutionId);

  const key =
    applicationId && !tokenHelper.jwtIsExpired()
      ? [paths.logs(applicationId), tokenHelper.getJwtToken()]
      : null;

  const { data, error, isValidating } = useSWR<{
    records: DisclosureLogRecord[];
  }>(key, fetchWithToken, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  return {
    logs: data?.records ?? [],
    isLoading: !error && !data && isValidating,
    error,
  };
}
